import React, { FC, useState, useEffect } from "react";
import { TaskRow } from "./task-row";
import { List, Button, Spin, Modal, Input, Space } from "antd";
import { PlusOutlined, ThunderboltOutlined, EditOutlined } from "@ant-design/icons";
import { Task } from "../../data-types";

import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableItem, DraggableHandle } from "../draggable-list";
import styles from "./task-list.module.css";
import { BEDROCK_GET_TASKS_FOR_PHASE_QUERY } from "~/graphql/query";
import { useLazyQuery } from "@apollo/client";
import { useSettings } from "~/hooks";
import { CustomPromptModal } from "../matter-relation-editor/custom-prompt-modal";
const {
  spin: spinClassName,
  "button-container": buttonContainerClassName,
  "primary-button": primaryButtonClassName,
  "custom-button": customButtonClassName,
} = styles as any;

type Props = {
  matterId: number;
  proposalTitle?: string;
  phaseId: number;
  tasks?: Task[] | null;
  onUpdate: (task: Partial<Task>) => void;
  onDelete: (id: number) => void;
  onCreate: () => void;
  onReorder: (activeId: number, overId: number) => void;
  onCreateTasks: (taskNames: string[]) => void;
  useAi?: boolean;
  autoGenerated?: boolean;
  countryCode?: string;
  onTimelineEdit: (context: {
    id: number;
    title: string;
    startDate?: Date | null;
    deadlineDate?: Date | null;
  }) => void;
};

export const TaskList: FC<Props> = ({
  matterId,
  proposalTitle,
  phaseId,
  tasks,
  onDelete,
  onUpdate,
  onCreate,
  onReorder,
  onCreateTasks,
  useAi,
  autoGenerated,
  countryCode,
  onTimelineEdit,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [tasksVersion, setTasksVersion] = useState(0);
  const [customModalVisible, setCustomModalVisible] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");

  const { settings } = useSettings();

  const [getTasks, { data: taskSuggestions, loading, error }] = useLazyQuery(
    BEDROCK_GET_TASKS_FOR_PHASE_QUERY,
    {
      variables: {
        matterId,
        phaseId,
        _version: tasksVersion,
        customPrompt: null,
        countryCode,
      } as any,
      fetchPolicy: "network-only",
    }
  );
  const [loadingTasks, setLoadingTasks] = useState(false);

  const handleAutoGenerate = async (
    useLibraryData: boolean,
    refineMode: boolean,
    customPrompt: string | null = null
  ) => {
    if (useAi && proposalTitle && getTasks) {
      setTasksVersion(tasksVersion + 1);
      setLoadingTasks(true);
      await getTasks({
        variables: {
          matterId,
          phaseId,
          _version: tasksVersion,
          customPrompt,
          refineMode,
          countryCode,
        } as any,
        fetchPolicy: "network-only",
      });
    }
  };

  useEffect(() => {
    if (taskSuggestions && taskSuggestions.bedrockGetTasksForPhase) {
      onCreateTasks(taskSuggestions.bedrockGetTasksForPhase.tasks);
    }
  }, [taskSuggestions]);

  useEffect(() => {
    if ((!loading && tasks && tasks.length > 0) || error) {
      setLoadingTasks(false);
    }
  }, [loading, tasks]);

  useEffect(() => {
    if (useAi && autoGenerated && (!tasks || tasks.length === 0)) {
      handleAutoGenerate(false, false, null);
    }
  }, []);

  return (
    <DndContext
      onDragStart={({ active }) =>
        setActiveTask(tasks?.find((p) => p.id.toString() === active.id)!)
      }
      onDragEnd={({ active, over }) => over && onReorder(parseInt(active.id), parseInt(over.id))}
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={tasks?.map((p) => p.id.toString()) || []}
        strategy={verticalListSortingStrategy}
      >
        {useAi ? (
          <>
            {loadingTasks ? (
              <Spin className={spinClassName} size="small" />
            ) : (
              <Space style={{ marginBottom: 10 }}>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={onCreate}>
                  Add Task
                </Button>
                {tasks?.length === 0 ? (
                  <div className={buttonContainerClassName}>
                    <Button
                      size="small"
                      type="primary"
                      className={primaryButtonClassName}
                      icon={<ThunderboltOutlined />}
                      onClick={() => handleAutoGenerate(false, false, null)}
                    >
                      First Draft
                    </Button>
                    <Button
                      size="small"
                      type="default"
                      className={customButtonClassName}
                      icon={<EditOutlined />}
                      onClick={() => setCustomModalVisible(true)}
                    >
                      Customise
                    </Button>
                  </div>
                ) : (
                  <div className={buttonContainerClassName}>
                    <Button
                      size="small"
                      type="primary"
                      className={primaryButtonClassName}
                      icon={<ThunderboltOutlined />}
                      onClick={() => handleAutoGenerate(false, false, null)}
                    >
                      Regenerate
                    </Button>
                    <Button
                      size="small"
                      type="default"
                      className={customButtonClassName}
                      icon={<EditOutlined />}
                      onClick={() => setCustomModalVisible(true)}
                    >
                      Refine
                    </Button>
                  </div>
                )}
              </Space>
            )}
          </>
        ) : (
          <Button
            style={{ marginBottom: 10 }}
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreate}
          >
            Add Task
          </Button>
        )}
        {!loadingTasks && (
          <List
            dataSource={tasks || []}
            split
            rowKey="id"
            renderItem={(item) => (
              <SortableItem id={item.id.toString()} key={item.id}>
                {({ attributes, listeners }) => (
                  <List.Item key={item.id}>
                    <TaskRow
                      defaultFont={settings?.systemFont ?? "Arial=arial"}
                      prefix={<DraggableHandle {...attributes} {...listeners} />}
                      task={item}
                      onDelete={onDelete}
                      onUpdate={onUpdate}
                      onTimelineEdit={onTimelineEdit}
                    />
                  </List.Item>
                )}
              </SortableItem>
            )}
          />
        )}
      </SortableContext>
      <DragOverlay>
        {activeTask ? (
          <List.Item key={activeTask.id}>
            <TaskRow
              defaultFont={settings?.systemFont ?? "Arial=arial"}
              prefix={<DraggableHandle />}
              task={activeTask}
              onDelete={onDelete}
              onUpdate={onUpdate}
              onTimelineEdit={onTimelineEdit}
            />
          </List.Item>
        ) : null}
      </DragOverlay>
      <CustomPromptModal
        customModalVisible={customModalVisible}
        customPrompt={customPrompt}
        setCustomPrompt={setCustomPrompt}
        setCustomModalVisible={setCustomModalVisible}
        handleAutoGenerate={handleAutoGenerate}
        refineMode={true}
      />
    </DndContext>
  );
};
